/**
 * DocsPage - Public documentation for the Vettis multi-agent research platform.
 *
 * Covers how agents collaborate, what blueprints are, how debate works,
 * and the research papers that underpin the system.
 *
 * Layout inspired by multiple.chat/docs: sticky sidebar TOC + scrollable content.
 * Uses the Verdict design system (dark theme, glassmorphism, amber gold accent).
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { SEO } from '@/components/SEO';
import {
  Scale,
  X,
  Users,
  Layers,
  Swords,
  BookOpen,
  ChevronRight,
  ExternalLink,
  TrendingUp,
  Target,
  Sparkles,
  ShieldCheck,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { SectionLabel, VerdictFooter } from '@/components/verdict';
import { PublicNavbar } from '@/components/layout';

/* ------------------------------------------------------------------ */
/*  Table of Contents structure                                        */
/* ------------------------------------------------------------------ */

interface TocItem {
  id: string;
  number: string;
  label: string;
}

interface TocSection {
  heading: string;
  items: TocItem[];
}

const TOC: TocSection[] = [
  {
    heading: '01. Overview',
    items: [
      { id: 'overview', number: '1.1', label: 'What Is Vettis?' },
      { id: 'architecture', number: '1.2', label: 'System Architecture' },
    ],
  },
  {
    heading: '02. Agent Council',
    items: [
      { id: 'how-agents-work', number: '2.1', label: 'How Agents Collaborate' },
      { id: 'agent-roles', number: '2.2', label: 'Specialized Roles' },
      { id: 'cross-verification', number: '2.3', label: 'Cross-Verification' },
    ],
  },
  {
    heading: '03. Research Blueprints',
    items: [
      { id: 'what-are-blueprints', number: '3.1', label: 'What Are Blueprints?' },
      { id: 'general-blueprint', number: '3.2', label: 'General Research' },
      { id: 'financial-blueprint', number: '3.3', label: 'Financial Deep Dive' },
      { id: 'strategic-blueprint', number: '3.4', label: 'Strategic Analysis' },
      { id: 'auto-detect-routing', number: '3.5', label: 'Auto-Detect Routing' },
    ],
  },
  {
    heading: '04. Debate Mechanism',
    items: [
      { id: 'debate-overview', number: '4.1', label: 'How Debate Works' },
      { id: 'consensus-debate', number: '4.2', label: 'Consensus Strategy' },
      { id: 'adversarial-debate', number: '4.3', label: 'Adversarial Strategy' },
      { id: 'mediator', number: '4.4', label: 'The Mediator' },
    ],
  },
  {
    heading: '05. Research Foundation',
    items: [
      { id: 'research-papers', number: '5.1', label: 'Cited Research' },
    ],
  },
];

/* ------------------------------------------------------------------ */
/*  Research papers (shared with BenchmarkPage)                        */
/* ------------------------------------------------------------------ */

const RESEARCH_PAPERS = [
  {
    title: 'Improving Factuality and Reasoning in Language Models through Multiagent Debate',
    authors: 'Yilun Du et al.',
    venue: 'MIT / ICML',
    year: 2023,
    url: 'https://arxiv.org/abs/2305.14325',
    relevance:
      'Demonstrates that multi-agent debate significantly reduces factual errors and improves reasoning accuracy over single-model outputs.',
    tag: 'Factuality',
  },
  {
    title: 'More Agents Is All You Need',
    authors: 'Junyou Li et al.',
    venue: 'Tsinghua University',
    year: 2024,
    url: 'https://arxiv.org/abs/2402.05120',
    relevance:
      'Shows that scaling the number of agents consistently improves performance, validating the multi-agent council approach.',
    tag: 'Scaling',
  },
  {
    title: 'Encouraging Divergent Thinking in Large Language Models through Multi-Agent Debate',
    authors: 'Tian Liang et al.',
    venue: 'ACL',
    year: 2023,
    url: 'https://arxiv.org/abs/2305.19118',
    relevance:
      'Proves that structured debate between agents produces more diverse, higher-quality reasoning than single-model chain-of-thought.',
    tag: 'Debate',
  },
  {
    title: 'Examining Inter-Consistency of Large Language Models Collaboration: An In-depth Analysis via Debate',
    authors: 'Kai Xiong et al.',
    venue: 'arXiv',
    year: 2023,
    url: 'https://arxiv.org/abs/2305.11595',
    relevance:
      'Analyzes how cross-agent debate catches contradictions and improves consistency, directly supporting conflict resolution claims.',
    tag: 'Consistency',
  },
  {
    title: 'Towards Reasoning in Large Language Models via Multi-Agent Peer Review Collaboration',
    authors: 'Zhenran Xu et al.',
    venue: 'arXiv',
    year: 2023,
    url: 'https://arxiv.org/abs/2311.08152',
    relevance:
      'Introduces peer-review collaboration between agents that mirrors our council verification process for improved reasoning.',
    tag: 'Peer Review',
  },
  {
    title: 'ChatEval: Towards Better LLM-based Evaluators through Multi-Agent Debate',
    authors: 'Chi-Min Chan et al.',
    venue: 'arXiv',
    year: 2023,
    url: 'https://arxiv.org/abs/2308.07201',
    relevance:
      'Shows multi-agent debate produces more reliable evaluations than single models, supporting our quality assurance approach.',
    tag: 'Evaluation',
  },
  {
    title: 'Multi-Agent Consensus Seeking via Large Language Models',
    authors: 'Huaben Chen et al.',
    venue: 'arXiv',
    year: 2023,
    url: 'https://arxiv.org/abs/2310.20151',
    relevance:
      'Formalizes consensus-seeking in multi-agent LLM systems, the theoretical foundation for our council output mechanism.',
    tag: 'Consensus',
  },
  {
    title: 'Large Language Model based Multi-Agents: A Survey of Progress and Challenges',
    authors: 'Taicheng Guo et al.',
    venue: 'IJCAI',
    year: 2024,
    url: 'https://arxiv.org/abs/2402.01680',
    relevance:
      'Comprehensive survey confirming multi-agent architectures outperform single models across problem-solving, reasoning, and verification tasks.',
    tag: 'Survey',
  },
];

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export function DocsPage() {
  const navigate = useNavigate();
  const [mobileTocOpen, setMobileTocOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('overview');

  /* ---- Intersection Observer for active TOC highlight ---- */
  useEffect(() => {
    const ids = TOC.flatMap((s) => s.items.map((i) => i.id));
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        }
      },
      { rootMargin: '-20% 0px -70% 0px', threshold: 0 }
    );
    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setMobileTocOpen(false);
    }
  };

  /* ================================================================ */
  /*  Render                                                          */
  /* ================================================================ */

  return (
    <div className="min-h-screen bg-background text-text-primary">
      <SEO
        title="Docs - Vettis"
        description="Vettis documentation. Learn how AI agents collaborate, use blueprints for custom research, and understand the multi-agent debate methodology."
        path="/docs"
      />
      {/* Background blurs */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute top-[-20%] left-[-10%] w-[600px] h-[600px] bg-verdict-primary/5 blur-[100px] rounded-full" />
        <div className="absolute bottom-[-30%] right-[-10%] w-[600px] h-[600px] bg-verdict-primary/5 blur-[100px] rounded-full" />
      </div>

      {/* Navigation */}
      <PublicNavbar currentPage="/docs" />

      {/* ---- Main layout: sidebar + content ---- */}
      <div className="relative z-10 pt-20 sm:pt-24 flex w-full px-4 sm:px-6 lg:px-8 xl:px-12 max-w-7xl mx-auto gap-10">

        {/* ---- Sidebar TOC (desktop) ---- */}
        <aside className="hidden lg:block w-64 shrink-0">
          <div className="sticky top-24 max-h-[calc(100vh-7rem)] overflow-y-auto pr-4 pb-12 scrollbar-thin scrollbar-thumb-white/10">
            {TOC.map((section) => (
              <div key={section.heading} className="mb-6">
                <h3 className="text-xs font-semibold tracking-widest text-verdict-primary/80 uppercase mb-2">
                  {section.heading}
                </h3>
                <nav className="flex flex-col gap-0.5">
                  {section.items.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => scrollTo(item.id)}
                      className={cn(
                        'flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg text-left transition-colors',
                        activeSection === item.id
                          ? 'bg-verdict-primary/10 text-verdict-primary'
                          : 'text-text-muted hover:text-text-primary hover:bg-surface-light'
                      )}
                    >
                      <span className="text-[10px] font-mono text-text-secondary/70 w-5">{item.number}</span>
                      <span>{item.label}</span>
                    </button>
                  ))}
                </nav>
              </div>
            ))}
          </div>
        </aside>

        {/* ---- Mobile TOC toggle ---- */}
        <button
          onClick={() => setMobileTocOpen(!mobileTocOpen)}
          className="lg:hidden fixed bottom-6 right-6 z-40 flex items-center gap-2 px-4 py-2.5 rounded-full bg-verdict-primary/20 border border-verdict-primary/40 text-verdict-primary text-sm backdrop-blur-lg shadow-lg"
        >
          <BookOpen className="w-4 h-4" />
          Contents
        </button>

        {/* Mobile TOC overlay */}
        {mobileTocOpen && (
          <div className="lg:hidden fixed inset-0 z-40 bg-black/80 backdrop-blur-sm" onClick={() => setMobileTocOpen(false)}>
            <div
              className="absolute bottom-0 left-0 right-0 max-h-[70vh] overflow-y-auto bg-surface border-t border-border-subtle rounded-t-2xl p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-verdict-primary">Table of Contents</h3>
                <button onClick={() => setMobileTocOpen(false)} className="p-1 hover:bg-surface-light rounded-lg"><X className="w-4 h-4 text-text-secondary" /></button>
              </div>
              {TOC.map((section) => (
                <div key={section.heading} className="mb-4">
                  <h4 className="text-xs font-semibold tracking-widest text-verdict-primary/80 uppercase mb-1">{section.heading}</h4>
                  <nav className="flex flex-col gap-0.5">
                    {section.items.map((item) => (
                      <button
                        key={item.id}
                        onClick={() => scrollTo(item.id)}
                        className={cn(
                          'flex items-center gap-2 px-3 py-2 text-sm rounded-lg text-left transition-colors',
                          activeSection === item.id
                            ? 'bg-verdict-primary/10 text-verdict-primary'
                            : 'text-text-secondary hover:text-text-primary hover:bg-surface-light'
                        )}
                      >
                        <span className="text-[10px] font-mono text-text-secondary/70 w-5">{item.number}</span>
                        <span>{item.label}</span>
                      </button>
                    ))}
                  </nav>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ---- Main content ---- */}
        <main className="flex-1 min-w-0 pb-24">
          {/* ===== 1.1 OVERVIEW ===== */}
          <section id="overview" className="scroll-mt-28 mb-16">
            <SectionLabel className="mb-4">DOCUMENTATION</SectionLabel>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-4">
              How <span className="text-verdict-primary">Vettis</span> Works
            </h1>
            <p className="text-text-secondary text-base sm:text-lg leading-relaxed max-w-3xl mb-8">
              Vettis is a multi-agent AI research platform that deploys a council of specialized
              AI agents to investigate any topic. Instead of relying on a single model,
              Vettis orchestrates multiple agents that research independently, cross-verify
              each other's findings, and engage in structured debate to produce higher-quality,
              more factual results.
            </p>

            {/* Feature cards */}
            <div className="grid sm:grid-cols-3 gap-4">
              <button type="button" onClick={() => scrollTo('how-agents-work')} className="verdict-card rounded-xl p-5 text-left cursor-pointer transition-colors hover:border-verdict-primary/30">
                <Users className="w-5 h-5 text-verdict-primary mb-3" />
                <h3 className="text-sm font-semibold text-text-primary mb-1">Agent Council</h3>
                <p className="text-xs text-text-secondary">Multiple specialized AI agents collaborate on every research query.</p>
              </button>
              <button type="button" onClick={() => scrollTo('what-are-blueprints')} className="verdict-card rounded-xl p-5 text-left cursor-pointer transition-colors hover:border-verdict-primary/30">
                <Layers className="w-5 h-5 text-verdict-primary mb-3" />
                <h3 className="text-sm font-semibold text-text-primary mb-1">Research Blueprints</h3>
                <p className="text-xs text-text-secondary">Predefined methodologies that define how research is conducted.</p>
              </button>
              <button type="button" onClick={() => scrollTo('debate-overview')} className="verdict-card rounded-xl p-5 text-left cursor-pointer transition-colors hover:border-verdict-primary/30">
                <Swords className="w-5 h-5 text-verdict-primary mb-3" />
                <h3 className="text-sm font-semibold text-text-primary mb-1">Structured Debate</h3>
                <p className="text-xs text-text-secondary">Agents argue opposing sides to stress-test conclusions.</p>
              </button>
            </div>

            {/* Fact-checking promotional callout */}
            <div className="mt-8 verdict-card rounded-xl p-6 border-verdict-primary/20 bg-gradient-to-r from-verdict-primary/5 via-transparent to-verdict-primary/5">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-verdict-primary/15 border border-verdict-primary/30 flex items-center justify-center shrink-0">
                  <ShieldCheck className="w-5 h-5 text-verdict-primary" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-text-primary mb-2">Built for Factual Accuracy</h3>
                  <p className="text-xs text-text-secondary leading-relaxed mb-3">
                    Every claim in a Vettis report is cross-verified by multiple agents, grounded in
                    primary sources, and stress-tested through adversarial debate. The result is
                    research you can actually trust &mdash; with full source traceability so you can
                    verify every finding yourself.
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <button type="button" onClick={() => scrollTo('cross-verification')} className="flex items-center gap-1.5 text-xs text-verdict-primary hover:underline cursor-pointer">
                      <ShieldCheck className="w-3.5 h-3.5" />
                      <span>Cross-Verification</span>
                    </button>
                    <button type="button" onClick={() => scrollTo('debate-overview')} className="flex items-center gap-1.5 text-xs text-verdict-primary hover:underline cursor-pointer">
                      <Swords className="w-3.5 h-3.5" />
                      <span>Adversarial Debate</span>
                    </button>
                    <button type="button" onClick={() => scrollTo('mediator')} className="flex items-center gap-1.5 text-xs text-verdict-primary hover:underline cursor-pointer">
                      <Scale className="w-3.5 h-3.5" />
                      <span>Evidence-Based Verdicts</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* ===== 1.2 ARCHITECTURE ===== */}
          <section id="architecture" className="scroll-mt-28 mb-16">
            <h2 className="text-2xl font-bold tracking-tight mb-4">
              System Architecture
            </h2>
            <p className="text-text-secondary text-sm leading-relaxed mb-6 max-w-3xl">
              When you submit a query, Vettis routes it through five distinct stages.
              Each stage is designed to maximize accuracy and minimize hallucinations.
            </p>
            <div className="verdict-card rounded-xl p-6">
              <ol className="space-y-4">
                {[
                  { step: '1', title: 'Blueprint Selection', desc: 'The auto-detect mechanism classifies your query and routes it to the appropriate blueprint (Financial, Strategic, or General). You can also manually select a blueprint.' },
                  { step: '2', title: 'Agent Assembly', desc: 'A council of specialized agents is assembled based on the blueprint\'s defined roles (e.g., Risk Analyst, Value Investor).' },
                  { step: '3', title: 'Independent Research', desc: 'Each agent researches the query independently, gathering data from designated sources like SEC filings, web search, and market data.' },
                  { step: '4', title: 'Cross-Verification & Debate', desc: 'Agents share findings, identify contradictions, and engage in structured debate to resolve disagreements.' },
                  { step: '5', title: 'Verdict Synthesis', desc: 'A mediator synthesizes the debate into a final verdict with confidence scores, citations, and caveats.' },
                ].map((item) => (
                  <li key={item.step} className="flex gap-4">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-verdict-primary/15 border border-verdict-primary/30 flex items-center justify-center text-verdict-primary text-sm font-bold">
                      {item.step}
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-text-primary">{item.title}</h4>
                      <p className="text-xs text-text-secondary mt-0.5">{item.desc}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </section>

          {/* ===== 2.1 HOW AGENTS COLLABORATE ===== */}
          <section id="how-agents-work" className="scroll-mt-28 mb-16 border-t border-border-subtle pt-12">
            <SectionLabel className="mb-4">AGENT COUNCIL</SectionLabel>
            <h2 className="text-2xl font-bold tracking-tight mb-4">
              How Agents <span className="text-verdict-primary">Collaborate</span>
            </h2>
            <p className="text-text-secondary text-sm leading-relaxed mb-6 max-w-3xl">
              Unlike single-model AI systems, Vettis deploys a council of 3-5 agents that
              each bring a distinct analytical lens. This mirrors how expert panels operate
              in the real world: diverse perspectives produce more robust conclusions.
            </p>

            <div className="grid sm:grid-cols-2 gap-4 mb-6">
              <div className="verdict-card rounded-xl p-5">
                <h4 className="text-sm font-semibold text-text-primary mb-2">Traditional Single-Model AI</h4>
                <ul className="space-y-1.5 text-xs text-text-secondary">
                  <li className="flex items-start gap-2"><span className="text-red-400 mt-0.5">&#x2717;</span> One perspective, limited self-critique</li>
                  <li className="flex items-start gap-2"><span className="text-red-400 mt-0.5">&#x2717;</span> No built-in fact verification</li>
                  <li className="flex items-start gap-2"><span className="text-red-400 mt-0.5">&#x2717;</span> Prone to confident hallucinations</li>
                  <li className="flex items-start gap-2"><span className="text-red-400 mt-0.5">&#x2717;</span> No adversarial stress-testing</li>
                </ul>
              </div>
              <div className="verdict-card rounded-xl p-5 border-verdict-primary/20">
                <h4 className="text-sm font-semibold text-verdict-primary mb-2">Vettis Multi-Agent Council</h4>
                <ul className="space-y-1.5 text-xs text-text-secondary">
                  <li className="flex items-start gap-2"><span className="text-verdict-primary mt-0.5">&#x2713;</span> Multiple specialized perspectives</li>
                  <li className="flex items-start gap-2"><span className="text-verdict-primary mt-0.5">&#x2713;</span> Cross-agent verification of findings</li>
                  <li className="flex items-start gap-2"><span className="text-verdict-primary mt-0.5">&#x2713;</span> Contradictions caught via debate</li>
                  <li className="flex items-start gap-2"><span className="text-verdict-primary mt-0.5">&#x2713;</span> Adversarial roles challenge assumptions</li>
                </ul>
              </div>
            </div>

            <div className="verdict-card rounded-xl p-5">
              <h4 className="text-xs font-semibold text-verdict-primary/80 uppercase tracking-wider mb-3">Research Backing</h4>
              <p className="text-xs text-text-secondary leading-relaxed">
                <em>"Scaling the number of agents in a system consistently improves performance"</em>{' '}
                &mdash; <a href="https://arxiv.org/abs/2402.05120" target="_blank" rel="noopener noreferrer" className="text-verdict-primary hover:underline">More Agents Is All You Need (2024)</a>.
                Vettis leverages this principle by assembling domain-specific agent councils rather than relying on a single model.
              </p>
            </div>
          </section>

          {/* ===== 2.2 SPECIALIZED ROLES ===== */}
          <section id="agent-roles" className="scroll-mt-28 mb-16">
            <h2 className="text-2xl font-bold tracking-tight mb-4">
              Specialized <span className="text-verdict-primary">Roles</span>
            </h2>
            <p className="text-text-secondary text-sm leading-relaxed mb-6 max-w-3xl">
              Each blueprint assigns agents distinct roles that enforce a particular
              analytical methodology. This prevents groupthink and ensures comprehensive coverage.
            </p>

            <div className="space-y-4">
              {/* General roles */}
              <div className="verdict-card rounded-xl p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles className="w-4 h-4 text-verdict-primary" />
                  <h4 className="text-sm font-semibold text-text-primary">General Blueprint Roles</h4>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {['Evidence Synthesizer', 'Framework Analyst', 'Devil\'s Advocate'].map((role) => (
                    <div key={role} className="px-3 py-2 rounded-lg bg-surface-light border border-border-subtle text-xs text-text-primary text-center">{role}</div>
                  ))}
                </div>
              </div>
              {/* Financial roles */}
              <div className="verdict-card rounded-xl p-5">
                <div className="flex items-center gap-2 mb-3">
                  <TrendingUp className="w-4 h-4 text-green-400" />
                  <h4 className="text-sm font-semibold text-text-primary">Financial Blueprint Roles</h4>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                  {['Risk Analyst', 'Value Investor', 'Growth Strategist', 'Market Technician', 'Macro Economist'].map((role) => (
                    <div key={role} className="px-3 py-2 rounded-lg bg-surface-light border border-border-subtle text-xs text-text-primary text-center">{role}</div>
                  ))}
                </div>
              </div>
              {/* Strategic roles */}
              <div className="verdict-card rounded-xl p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Target className="w-4 h-4 text-blue-400" />
                  <h4 className="text-sm font-semibold text-text-primary">Strategic Blueprint Roles</h4>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                  {['Strategy Lead', 'Market Analyst', 'Competitive Intelligence', 'Business Model Expert', 'Operations & Risk'].map((role) => (
                    <div key={role} className="px-3 py-2 rounded-lg bg-surface-light border border-border-subtle text-xs text-text-primary text-center">{role}</div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* ===== 2.3 CROSS-VERIFICATION ===== */}
          <section id="cross-verification" className="scroll-mt-28 mb-16">
            <h2 className="text-2xl font-bold tracking-tight mb-4">
              Cross-<span className="text-verdict-primary">Verification</span>
            </h2>
            <p className="text-text-secondary text-sm leading-relaxed mb-6 max-w-3xl">
              After independent research, agents share their findings with the council.
              The system identifies areas of agreement and disagreement. When agents
              contradict each other, a debate is triggered to resolve the conflict.
            </p>
            <div className="verdict-card rounded-xl p-5">
              <h4 className="text-xs font-semibold text-verdict-primary/80 uppercase tracking-wider mb-3">How It Works</h4>
              <ul className="space-y-2 text-xs text-text-secondary">
                <li className="flex items-start gap-2"><ChevronRight className="w-3.5 h-3.5 text-verdict-primary mt-0.5 shrink-0" /> Each agent submits findings with <strong className="text-text-primary">confidence scores</strong></li>
                <li className="flex items-start gap-2"><ChevronRight className="w-3.5 h-3.5 text-verdict-primary mt-0.5 shrink-0" /> The system compares claims across agents for <strong className="text-text-primary">consistency</strong></li>
                <li className="flex items-start gap-2"><ChevronRight className="w-3.5 h-3.5 text-verdict-primary mt-0.5 shrink-0" /> Contradictions trigger debate; low confidence triggers further research</li>
                <li className="flex items-start gap-2"><ChevronRight className="w-3.5 h-3.5 text-verdict-primary mt-0.5 shrink-0" /> Sources are cited as <code className="text-verdict-primary bg-verdict-primary/10 px-1 rounded">[S#]</code>, <code className="text-verdict-primary bg-verdict-primary/10 px-1 rounded">[FMP]</code>, <code className="text-verdict-primary bg-verdict-primary/10 px-1 rounded">[SEC]</code>, <code className="text-verdict-primary bg-verdict-primary/10 px-1 rounded">[NEWS]</code> for traceability</li>
              </ul>
            </div>
          </section>

          {/* ===== 3.1 WHAT ARE BLUEPRINTS ===== */}
          <section id="what-are-blueprints" className="scroll-mt-28 mb-16 border-t border-border-subtle pt-12">
            <SectionLabel className="mb-4">RESEARCH BLUEPRINTS</SectionLabel>
            <h2 className="text-2xl font-bold tracking-tight mb-4">
              What Are <span className="text-verdict-primary">Blueprints</span>?
            </h2>
            <p className="text-text-secondary text-sm leading-relaxed mb-6 max-w-3xl">
              A Research Blueprint defines <strong className="text-text-primary">how</strong> research
              is conducted. It controls the agent roles, debate framing, data sources, and output
              format. Think of it as a research methodology template that the council follows.
            </p>

            <div className="verdict-card rounded-xl p-6 mb-6">
              <h4 className="text-sm font-semibold text-text-primary mb-4">Blueprint Controls</h4>
              <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3 text-xs">
                <div className="flex items-start gap-2">
                  <span className="text-verdict-primary font-bold">Agent Roles</span>
                  <span className="text-text-muted">&mdash;</span>
                  <span className="text-text-secondary">Who is on the council and what lens they apply</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-verdict-primary font-bold">Debate Framing</span>
                  <span className="text-text-muted">&mdash;</span>
                  <span className="text-text-secondary">How the two sides of the debate are named</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-verdict-primary font-bold">Data Sources</span>
                  <span className="text-text-muted">&mdash;</span>
                  <span className="text-text-secondary">Which APIs and databases agents pull from</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-verdict-primary font-bold">Output Type</span>
                  <span className="text-text-muted">&mdash;</span>
                  <span className="text-text-secondary">Kill Sheet, Strategic Deliverable, or Key Findings</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-verdict-primary font-bold">Always Debate</span>
                  <span className="text-text-muted">&mdash;</span>
                  <span className="text-text-secondary">Whether debate runs every time or only when confidence is low</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-verdict-primary font-bold">Capability</span>
                  <span className="text-text-muted">&mdash;</span>
                  <span className="text-text-secondary">Model routing hint (reasoning, creative, factual)</span>
                </div>
              </div>
            </div>
          </section>

          {/* ===== 3.2 GENERAL BLUEPRINT ===== */}
          <section id="general-blueprint" className="scroll-mt-28 mb-16">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 rounded-lg bg-verdict-primary/15 border border-verdict-primary/30 flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-verdict-primary" />
              </div>
              <h2 className="text-2xl font-bold tracking-tight">General Research</h2>
            </div>
            <p className="text-text-secondary text-sm leading-relaxed mb-6 max-w-3xl">
              The default research blueprint for any topic that does not require specialized
              financial or strategic analysis. Uses a balanced team of three methodologically
              diverse agents that emphasize evidence synthesis, structured frameworks, and
              adversarial challenge. This is the fallback blueprint when no specific mode is selected.
            </p>

            <div className="verdict-card rounded-xl overflow-hidden">
              <div className="px-5 py-3 border-b border-border-subtle bg-verdict-primary/5">
                <span className="text-xs font-mono text-verdict-primary">blueprint_id: &quot;general&quot;</span>
              </div>
              <div className="p-5 space-y-4 text-xs">
                <div>
                  <span className="text-text-muted uppercase tracking-wider text-[10px]">Debate Framing</span>
                  <div className="flex gap-3 mt-1">
                    <span className="px-2.5 py-1 rounded-md bg-verdict-primary/10 text-verdict-primary border border-verdict-primary/20">Supporting Evidence</span>
                    <span className="text-text-muted">vs</span>
                    <span className="px-2.5 py-1 rounded-md bg-surface-light text-text-secondary border border-border-subtle">Counter Evidence</span>
                  </div>
                </div>
                <div>
                  <span className="text-text-muted uppercase tracking-wider text-[10px]">Data Sources</span>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {['Web Search', 'News'].map((src) => (
                      <span key={src} className="px-2 py-1 rounded-md bg-surface-light text-text-secondary border border-border-subtle">{src}</span>
                    ))}
                  </div>
                </div>
                <div>
                  <span className="text-text-muted uppercase tracking-wider text-[10px]">Output</span>
                  <p className="text-text-primary mt-1">Key Findings &mdash; balanced synthesis with confidence scores</p>
                </div>
                <div>
                  <span className="text-text-muted uppercase tracking-wider text-[10px]">Always Debates</span>
                  <p className="text-text-secondary mt-1">No &mdash; debates only when agent confidence drops below 0.7</p>
                </div>
              </div>
            </div>
          </section>

          {/* ===== 3.3 FINANCIAL BLUEPRINT ===== */}
          <section id="financial-blueprint" className="scroll-mt-28 mb-16">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 rounded-lg bg-green-500/15 border border-green-500/30 flex items-center justify-center">
                <TrendingUp className="w-4 h-4 text-green-400" />
              </div>
              <h2 className="text-2xl font-bold tracking-tight">Financial Deep Dive</h2>
            </div>
            <p className="text-text-secondary text-sm leading-relaxed mb-6 max-w-3xl">
              Bull vs Bear analysis with a Kill Sheet for investment decisions. This blueprint
              <strong className="text-text-primary"> always runs debate</strong> to ensure thorough
              examination of both bullish and bearish perspectives.
            </p>

            <div className="verdict-card rounded-xl overflow-hidden">
              <div className="px-5 py-3 border-b border-border-subtle bg-green-500/5">
                <span className="text-xs font-mono text-green-400">blueprint_id: &quot;financial&quot;</span>
              </div>
              <div className="p-5 space-y-4 text-xs">
                <div>
                  <span className="text-text-muted uppercase tracking-wider text-[10px]">Debate Framing</span>
                  <div className="flex gap-3 mt-1">
                    <span className="px-2.5 py-1 rounded-md bg-green-500/10 text-green-400 border border-green-500/20">Bull Case</span>
                    <span className="text-text-muted">vs</span>
                    <span className="px-2.5 py-1 rounded-md bg-red-500/10 text-red-400 border border-red-500/20">Bear Case</span>
                  </div>
                </div>
                <div>
                  <span className="text-text-muted uppercase tracking-wider text-[10px]">Data Sources</span>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {['Financial Modeling Prep', 'SEC Edgar', 'Web Search', 'Finnhub'].map((src) => (
                      <span key={src} className="px-2 py-1 rounded-md bg-surface-light text-text-secondary border border-border-subtle">{src}</span>
                    ))}
                  </div>
                </div>
                <div>
                  <span className="text-text-muted uppercase tracking-wider text-[10px]">Output</span>
                  <p className="text-text-primary mt-1">Kill Sheet &mdash; includes stance, catalysts, risks, and watch items</p>
                </div>
                <div>
                  <span className="text-text-muted uppercase tracking-wider text-[10px]">Always Debates</span>
                  <p className="text-green-400 mt-1">Yes &mdash; adversarial debate runs on every query</p>
                </div>
              </div>
            </div>
          </section>

          {/* ===== 3.4 STRATEGIC BLUEPRINT ===== */}
          <section id="strategic-blueprint" className="scroll-mt-28 mb-16">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 rounded-lg bg-blue-500/15 border border-blue-500/30 flex items-center justify-center">
                <Target className="w-4 h-4 text-blue-400" />
              </div>
              <h2 className="text-2xl font-bold tracking-tight">Strategic Analysis</h2>
            </div>
            <p className="text-text-secondary text-sm leading-relaxed mb-6 max-w-3xl">
              Consultant-grade strategic analysis with MECE workstream decomposition,
              explicit assumptions, and evidence-first synthesis. Modeled after top-tier
              management consulting methodology (McKinsey/Bain/BCG approach).
            </p>

            <div className="verdict-card rounded-xl overflow-hidden">
              <div className="px-5 py-3 border-b border-border-subtle bg-blue-500/5">
                <span className="text-xs font-mono text-blue-400">blueprint_id: &quot;strategic&quot;</span>
              </div>
              <div className="p-5 space-y-4 text-xs">
                <div>
                  <span className="text-text-muted uppercase tracking-wider text-[10px]">Debate Framing</span>
                  <div className="flex gap-3 mt-1">
                    <span className="px-2.5 py-1 rounded-md bg-blue-500/10 text-blue-400 border border-blue-500/20">Why This Strategy Will Succeed</span>
                    <span className="text-text-muted">vs</span>
                    <span className="px-2.5 py-1 rounded-md bg-orange-500/10 text-orange-400 border border-orange-500/20">Why It Will Fail</span>
                  </div>
                </div>
                <div>
                  <span className="text-text-muted uppercase tracking-wider text-[10px]">Data Sources</span>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {['Web Search', 'Industry Reports', 'Competitor Data', 'Company Filings', 'News Analysis'].map((src) => (
                      <span key={src} className="px-2 py-1 rounded-md bg-surface-light text-text-secondary border border-border-subtle">{src}</span>
                    ))}
                  </div>
                </div>
                <div>
                  <span className="text-text-muted uppercase tracking-wider text-[10px]">Output</span>
                  <p className="text-text-primary mt-1">Strategic Deliverable &mdash; executive summary, MECE workstreams, ghost deck structure</p>
                </div>
                <div>
                  <span className="text-text-muted uppercase tracking-wider text-[10px]">Always Debates</span>
                  <p className="text-text-secondary mt-1">No &mdash; debates only when confidence is low or contradictions are detected</p>
                </div>
                <div>
                  <span className="text-text-muted uppercase tracking-wider text-[10px]">Key Principles</span>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {['MECE Decomposition', 'Evidence First', 'Assumption Testing', 'Steel-man Arguments', 'So What Focus'].map((p) => (
                      <span key={p} className="px-2 py-1 rounded-md bg-blue-500/5 text-blue-300 border border-blue-500/10">{p}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* ===== 3.5 AUTO-DETECT ROUTING ===== */}
          <section id="auto-detect-routing" className="scroll-mt-28 mb-16">
            <h2 className="text-2xl font-bold tracking-tight mb-4">
              Auto-Detect <span className="text-verdict-primary">Routing</span>
            </h2>
            <p className="text-text-secondary text-sm leading-relaxed mb-6 max-w-3xl">
              Auto-Detect is not a blueprint itself &mdash; it is a <strong className="text-text-primary">routing mechanism</strong> that
              analyzes your query and selects the most appropriate blueprint. When you submit a query
              without manually choosing a blueprint, the auto-detect system classifies your topic and
              routes it to Financial, Strategic, or General.
            </p>

            <div className="verdict-card rounded-xl p-6 mb-6">
              <h4 className="text-sm font-semibold text-text-primary mb-4">How Auto-Detect Routes Your Query</h4>
              <div className="space-y-3 text-xs">
                <div className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded bg-green-500/15 border border-green-500/30 flex items-center justify-center shrink-0 mt-0.5">
                    <TrendingUp className="w-3 h-3 text-green-400" />
                  </div>
                  <div>
                    <span className="text-green-400 font-semibold">Financial</span>
                    <span className="text-text-muted mx-1">&mdash;</span>
                    <span className="text-text-secondary">Query mentions a <strong className="text-text-primary">specific stock ticker</strong> or company name (e.g., &quot;Analyze NVDA&quot;, &quot;Should I buy Tesla?&quot;)</span>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded bg-blue-500/15 border border-blue-500/30 flex items-center justify-center shrink-0 mt-0.5">
                    <Target className="w-3 h-3 text-blue-400" />
                  </div>
                  <div>
                    <span className="text-blue-400 font-semibold">Strategic</span>
                    <span className="text-text-muted mx-1">&mdash;</span>
                    <span className="text-text-secondary">Business strategy, competitive analysis, market entry questions (currently requires manual selection)</span>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded bg-verdict-primary/15 border border-verdict-primary/30 flex items-center justify-center shrink-0 mt-0.5">
                    <Sparkles className="w-3 h-3 text-verdict-primary" />
                  </div>
                  <div>
                    <span className="text-verdict-primary font-semibold">General</span>
                    <span className="text-text-muted mx-1">&mdash;</span>
                    <span className="text-text-secondary">Everything else: technology, science, news, general investment questions without a specific ticker</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="verdict-card rounded-xl p-5">
              <h4 className="text-xs font-semibold text-verdict-primary/80 uppercase tracking-wider mb-3">Guardrails</h4>
              <ul className="space-y-2 text-xs text-text-secondary">
                <li className="flex items-start gap-2">
                  <ChevronRight className="w-3.5 h-3.5 text-verdict-primary mt-0.5 shrink-0" />
                  <span><strong className="text-text-primary">Ticker validation:</strong> Financial mode requires a specific stock ticker. If no ticker is found, the system falls back to General.</span>
                </li>
                <li className="flex items-start gap-2">
                  <ChevronRight className="w-3.5 h-3.5 text-verdict-primary mt-0.5 shrink-0" />
                  <span><strong className="text-text-primary">Alpha protection:</strong> Blueprints tagged as alpha (like Strategic) are excluded from auto-detection. Users must manually select them.</span>
                </li>
                <li className="flex items-start gap-2">
                  <ChevronRight className="w-3.5 h-3.5 text-verdict-primary mt-0.5 shrink-0" />
                  <span><strong className="text-text-primary">Safe default:</strong> When in doubt, the system always falls back to General &mdash; the safest and most versatile blueprint.</span>
                </li>
              </ul>
            </div>
          </section>

          {/* ===== 4.1 DEBATE OVERVIEW ===== */}
          <section id="debate-overview" className="scroll-mt-28 mb-16 border-t border-border-subtle pt-12">
            <SectionLabel className="mb-4">DEBATE MECHANISM</SectionLabel>
            <h2 className="text-2xl font-bold tracking-tight mb-4">
              How <span className="text-verdict-primary">Debate</span> Works
            </h2>
            <p className="text-text-secondary text-sm leading-relaxed mb-6 max-w-3xl">
              Debate is the core quality-assurance mechanism in Vettis. When agents disagree
              or confidence is low, the system triggers a multi-round debate where agents argue
              opposing positions. A neutral mediator then synthesizes the arguments into a final verdict.
            </p>

            <div className="verdict-card rounded-xl p-6 mb-6">
              <h4 className="text-sm font-semibold text-text-primary mb-4">Debate Flow</h4>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-0 text-xs">
                {[
                  { label: 'Side A argues', color: 'text-green-400' },
                  { label: 'Side B counters', color: 'text-red-400' },
                  { label: 'Side A rebuts', color: 'text-green-400' },
                  { label: 'Side B rebuts', color: 'text-red-400' },
                  { label: 'Mediator verdict', color: 'text-verdict-primary' },
                ].map((step, i) => (
                  <div key={i} className="flex items-center gap-2">
                    {i > 0 && <ChevronRight className="hidden sm:block w-3.5 h-3.5 text-text-muted" />}
                    <span className={cn('px-3 py-1.5 rounded-lg border border-border-subtle bg-surface-light', step.color)}>
                      {step.label}
                    </span>
                  </div>
                ))}
              </div>
              <p className="text-text-muted text-[10px] mt-3">Up to 3 rounds. Debate ends early if consensus is reached.</p>
            </div>

            <div className="verdict-card rounded-xl p-5">
              <h4 className="text-xs font-semibold text-verdict-primary/80 uppercase tracking-wider mb-3">Research Backing</h4>
              <p className="text-xs text-text-secondary leading-relaxed">
                <em>"Multi-agent debate significantly reduces factual errors and improves reasoning accuracy"</em>{' '}
                &mdash; <a href="https://arxiv.org/abs/2305.14325" target="_blank" rel="noopener noreferrer" className="text-verdict-primary hover:underline">Du et al., MIT/ICML (2023)</a>.
                Structured adversarial debate forces agents to justify claims with evidence rather than relying on
                parametric memory alone.
              </p>
            </div>
          </section>

          {/* ===== 4.2 CONSENSUS STRATEGY ===== */}
          <section id="consensus-debate" className="scroll-mt-28 mb-16">
            <h2 className="text-2xl font-bold tracking-tight mb-4">
              Consensus <span className="text-verdict-primary">Strategy</span>
            </h2>
            <p className="text-text-secondary text-sm leading-relaxed mb-6 max-w-3xl">
              Used by the General and Strategic blueprints. Seeks agreement when
              agent confidence drops below a threshold (default: 0.7). The goal is to find
              the truth, not to win an argument.
            </p>
            <div className="verdict-card rounded-xl p-5 space-y-3 text-xs">
              <div className="flex items-start gap-2">
                <ChevronRight className="w-3.5 h-3.5 text-verdict-primary mt-0.5 shrink-0" />
                <span className="text-text-secondary"><strong className="text-text-primary">Trigger:</strong> Confidence &lt; 0.7 after initial research</span>
              </div>
              <div className="flex items-start gap-2">
                <ChevronRight className="w-3.5 h-3.5 text-verdict-primary mt-0.5 shrink-0" />
                <span className="text-text-secondary"><strong className="text-text-primary">Side A:</strong> Reviews findings, identifies points of agreement, proposes synthesis</span>
              </div>
              <div className="flex items-start gap-2">
                <ChevronRight className="w-3.5 h-3.5 text-verdict-primary mt-0.5 shrink-0" />
                <span className="text-text-secondary"><strong className="text-text-primary">Side B:</strong> Challenges assumptions, proposes alternative interpretations, identifies evidence gaps</span>
              </div>
              <div className="flex items-start gap-2">
                <ChevronRight className="w-3.5 h-3.5 text-verdict-primary mt-0.5 shrink-0" />
                <span className="text-text-secondary"><strong className="text-text-primary">Mediator:</strong> Acknowledges valid points from both sides, identifies strongest evidence, notes remaining uncertainties</span>
              </div>
            </div>
          </section>

          {/* ===== 4.3 ADVERSARIAL STRATEGY ===== */}
          <section id="adversarial-debate" className="scroll-mt-28 mb-16">
            <h2 className="text-2xl font-bold tracking-tight mb-4">
              Adversarial <span className="text-verdict-primary">Strategy</span>
            </h2>
            <p className="text-text-secondary text-sm leading-relaxed mb-6 max-w-3xl">
              Used by the Financial blueprint. Adversarial debate <strong className="text-text-primary">always runs</strong>,
              regardless of confidence. Agents are assigned to Bull and Bear teams and instructed
              to <em>win</em> the argument, not to seek consensus. This produces the strongest
              possible case for both sides.
            </p>
            <div className="verdict-card rounded-xl p-5 space-y-3 text-xs">
              <div className="flex items-start gap-2">
                <ChevronRight className="w-3.5 h-3.5 text-green-400 mt-0.5 shrink-0" />
                <span className="text-text-secondary"><strong className="text-green-400">Bull Side:</strong> Identifies the strongest investment thesis, cites growth drivers, valuation upside, and positive catalysts</span>
              </div>
              <div className="flex items-start gap-2">
                <ChevronRight className="w-3.5 h-3.5 text-red-400 mt-0.5 shrink-0" />
                <span className="text-text-secondary"><strong className="text-red-400">Bear Side:</strong> Attacks the bull thesis with risk factors, overvaluation metrics, competitive threats, and macro headwinds</span>
              </div>
              <div className="flex items-start gap-2">
                <ChevronRight className="w-3.5 h-3.5 text-verdict-primary mt-0.5 shrink-0" />
                <span className="text-text-secondary"><strong className="text-text-primary">Rebuttals:</strong> Each side must <em>destroy</em> the opponent's argument &mdash; agreeing to disagree is not allowed</span>
              </div>
              <div className="flex items-start gap-2">
                <ChevronRight className="w-3.5 h-3.5 text-verdict-primary mt-0.5 shrink-0" />
                <span className="text-text-secondary"><strong className="text-text-primary">Winner:</strong> The mediator picks a winner based on evidence quality, counter-argument effectiveness, and assumption reasonableness</span>
              </div>
            </div>

            <div className="verdict-card rounded-xl p-5 mt-4">
              <h4 className="text-xs font-semibold text-verdict-primary/80 uppercase tracking-wider mb-3">Research Backing</h4>
              <p className="text-xs text-text-secondary leading-relaxed">
                <em>"Structured debate between agents produces more diverse, higher-quality reasoning than single-model chain-of-thought"</em>{' '}
                &mdash; <a href="https://arxiv.org/abs/2305.19118" target="_blank" rel="noopener noreferrer" className="text-verdict-primary hover:underline">Liang et al., ACL (2023)</a>.
                The adversarial framing forces each side to present its absolute strongest case, preventing groupthink.
              </p>
            </div>
          </section>

          {/* ===== 4.4 THE MEDIATOR ===== */}
          <section id="mediator" className="scroll-mt-28 mb-16">
            <h2 className="text-2xl font-bold tracking-tight mb-4">
              The <span className="text-verdict-primary">Mediator</span>
            </h2>
            <p className="text-text-secondary text-sm leading-relaxed mb-6 max-w-3xl">
              After all debate rounds conclude, a neutral mediator agent synthesizes the
              arguments into a final verdict. The mediator does not advocate for either side
              &mdash; it evaluates evidence quality, logical consistency, and assumption
              reasonableness to determine which arguments hold up.
            </p>
            <div className="verdict-card rounded-xl p-5 space-y-3 text-xs">
              <div className="flex items-start gap-2">
                <ChevronRight className="w-3.5 h-3.5 text-verdict-primary mt-0.5 shrink-0" />
                <span className="text-text-secondary"><strong className="text-text-primary">Evidence-based:</strong> Evaluates which side had better supporting data from primary sources</span>
              </div>
              <div className="flex items-start gap-2">
                <ChevronRight className="w-3.5 h-3.5 text-verdict-primary mt-0.5 shrink-0" />
                <span className="text-text-secondary"><strong className="text-text-primary">Counter-argument effectiveness:</strong> Weighs how well each side responded to challenges</span>
              </div>
              <div className="flex items-start gap-2">
                <ChevronRight className="w-3.5 h-3.5 text-verdict-primary mt-0.5 shrink-0" />
                <span className="text-text-secondary"><strong className="text-text-primary">Confidence score:</strong> Outputs a 0.0 - 1.0 confidence rating for the verdict</span>
              </div>
              <div className="flex items-start gap-2">
                <ChevronRight className="w-3.5 h-3.5 text-verdict-primary mt-0.5 shrink-0" />
                <span className="text-text-secondary"><strong className="text-text-primary">Rejected arguments:</strong> Lists arguments that were refuted, with reasons (weak evidence, contradicted by data, logical flaw)</span>
              </div>
              <div className="flex items-start gap-2">
                <ChevronRight className="w-3.5 h-3.5 text-verdict-primary mt-0.5 shrink-0" />
                <span className="text-text-secondary"><strong className="text-text-primary">Caveats:</strong> Notes what could make the losing side correct in the future</span>
              </div>
            </div>
          </section>

          {/* ===== 5.1 RESEARCH PAPERS ===== */}
          <section id="research-papers" className="scroll-mt-28 mb-16 border-t border-border-subtle pt-12">
            <SectionLabel className="mb-4">RESEARCH FOUNDATION</SectionLabel>
            <h2 className="text-2xl font-bold tracking-tight mb-4">
              Cited <span className="text-verdict-primary">Research</span>
            </h2>
            <p className="text-text-secondary text-sm leading-relaxed mb-6 max-w-3xl">
              Vettis is built on peer-reviewed research from MIT, Tsinghua, IJCAI, ACL,
              and other leading institutions. These papers provide the theoretical foundation
              for why multi-agent debate and council-based architectures outperform single models.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {RESEARCH_PAPERS.map((paper) => (
                <a
                  key={paper.title}
                  href={paper.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="verdict-card rounded-xl p-5 group hover:border-verdict-primary/30 transition-colors block"
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[10px] font-mono uppercase tracking-wider text-verdict-primary/80 bg-verdict-primary/10 px-2 py-0.5 rounded">
                      {paper.tag}
                    </span>
                    <ExternalLink className="w-3.5 h-3.5 text-text-muted group-hover:text-verdict-primary transition-colors" />
                  </div>
                  <h3 className="text-sm font-semibold text-text-primary group-hover:text-text-primary transition-colors leading-snug mb-1">
                    {paper.title}
                  </h3>
                  <p className="text-xs text-text-muted mb-3">
                    {paper.authors} &middot; {paper.venue} {paper.year}
                  </p>
                  <p className="text-xs text-text-secondary leading-relaxed">{paper.relevance}</p>
                </a>
              ))}
            </div>

            {/* CTA */}
            <div className="text-center mt-10">
              <button
                onClick={() => navigate('/benchmark')}
                className="inline-flex items-center gap-2 px-6 py-2.5 text-sm font-medium text-verdict-primary hover:underline transition-colors"
              >
                View full benchmark results <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </section>
        </main>
      </div>

      <VerdictFooter />
    </div>
  );
}

